import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { products as mockProducts } from '../../data/mockData';
import {
  Diamond, LogOut, Package, ShoppingBag, DollarSign, Users,
  Plus, Pencil, Trash2, X, Save, Eye, ChevronDown, ChevronUp, Upload, Image as ImageIcon, Loader2
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API_URL = '';

const AdminDashboard = () => {
  const { isAdmin, adminLogout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState(() => {
    const saved = localStorage.getItem('passlux_admin_products');
    return saved ? JSON.parse(saved) : mockProducts;
  });
  const [orders, setOrders] = useState(() => {
    const saved = localStorage.getItem('passlux_orders');
    return saved ? JSON.parse(saved) : [];
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);

  useEffect(() => {
    if (!isAdmin) navigate('/admin/login');
  }, [isAdmin, navigate]);

  useEffect(() => {
    localStorage.setItem('passlux_admin_products', JSON.stringify(products));
  }, [products]);

  const emptyProduct = {
    id: '', name: '', slug: '', price: 0, category: 'watch-accessories',
    categoryName: 'Watch Accessories', image: '', description: '', features: [], inStock: true, featured: false
  };

  const categoryOptions = [
    { value: 'watch-accessories', label: 'Watch Accessories' },
    { value: 'watch-straps', label: 'Watch Straps' },
    { value: 'phone-cases', label: 'Phone Cases' },
    { value: 'jewelry-accessories', label: 'Jewelry Accessories' },
  ];

  const handleSaveProduct = (product) => {
    if (editingProduct) {
      setProducts(prev => prev.map(p => p.id === product.id ? product : p));
      toast({ title: 'Product updated' });
    } else {
      const newP = { ...product, id: Date.now().toString(), slug: product.name.toLowerCase().replace(/\s+/g, '-') };
      setProducts(prev => [...prev, newP]);
      toast({ title: 'Product created' });
    }
    setEditingProduct(null);
    setShowProductForm(false);
  };

  const handleDeleteProduct = (id) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    toast({ title: 'Product deleted' });
  };

  const handleUpdateOrderStatus = (orderId, newStatus) => {
    const updated = orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o);
    setOrders(updated);
    localStorage.setItem('passlux_orders', JSON.stringify(updated));
    toast({ title: `Order ${orderId} updated to ${newStatus}` });
  };

  const totalRevenue = orders.reduce((sum, o) => sum + o.total, 0);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Diamond },
    { id: 'products', label: 'Products', icon: ShoppingBag },
    { id: 'orders', label: 'Orders', icon: Package },
  ];

  const inputCls = "w-full bg-[#0a0a0a] border border-gray-800 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-[#2EC866]/50 transition-colors";

  if (!isAdmin) return null;

  return (
    <div className="bg-[#0a0a0a] min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#111] border-r border-gray-800/50 fixed top-0 left-0 h-full z-40 flex flex-col">
        <div className="p-6 border-b border-gray-800/50">
          <div className="flex items-center gap-2">
            <Diamond className="w-6 h-6 text-[#2EC866]" />
            <div>
              <p className="text-white font-bold text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>LUXO-MARKET</p>
              <p className="text-gray-600 text-[10px] uppercase tracking-wider">Back Office</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#2EC866]/10 text-[#2EC866] font-medium'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-800/50">
          <button onClick={() => { adminLogout(); navigate('/admin/login'); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-gray-400 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            <h1 className="text-2xl font-bold text-white mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>Dashboard</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: 'Total Products', value: products.length, icon: ShoppingBag, color: 'text-blue-400' },
                { label: 'Total Orders', value: orders.length, icon: Package, color: 'text-purple-400' },
                { label: 'Revenue', value: `\u00A3${totalRevenue.toFixed(2)}`, icon: DollarSign, color: 'text-[#2EC866]' },
                { label: 'Customers', value: new Set(orders.map(o => o.customerEmail)).size, icon: Users, color: 'text-orange-400' },
              ].map((stat, i) => (
                <div key={i} className="bg-[#111] border border-gray-800/50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-gray-500 text-xs mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#111] border border-gray-800/50 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Recent Orders</h3>
              {orders.slice(-5).reverse().map(order => (
                <div key={order.id} className="flex items-center justify-between py-3 border-b border-gray-800/30 last:border-0">
                  <div>
                    <p className="text-white text-sm font-mono">{order.id}</p>
                    <p className="text-gray-500 text-xs">{order.customerName}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-sm">&pound;{order.total.toFixed(2)}</p>
                    <span className={`text-xs font-medium ${
                      order.status === 'delivered' ? 'text-green-400' :
                      order.status === 'shipped' ? 'text-blue-400' : 'text-yellow-400'
                    }`}>{order.status}</span>
                  </div>
                </div>
              ))}
              {orders.length === 0 && <p className="text-gray-500 text-sm">No orders yet</p>}
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Products</h1>
              <button
                onClick={() => { setEditingProduct(null); setShowProductForm(true); }}
                className="flex items-center gap-2 bg-[#2EC866] hover:bg-[#25a855] text-white font-medium px-4 py-2.5 rounded-lg text-sm transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Product
              </button>
            </div>

            {/* Product Form Modal */}
            {showProductForm && (
              <ProductForm
                product={editingProduct || emptyProduct}
                categories={categoryOptions}
                inputCls={inputCls}
                onSave={handleSaveProduct}
                onCancel={() => { setShowProductForm(false); setEditingProduct(null); }}
                isEditing={!!editingProduct}
              />
            )}

            {/* Products Table */}
            <div className="bg-[#111] border border-gray-800/50 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left text-gray-500 text-xs uppercase tracking-wider px-6 py-4">Product</th>
                      <th className="text-left text-gray-500 text-xs uppercase tracking-wider px-6 py-4">Category</th>
                      <th className="text-left text-gray-500 text-xs uppercase tracking-wider px-6 py-4">Price</th>
                      <th className="text-left text-gray-500 text-xs uppercase tracking-wider px-6 py-4">Status</th>
                      <th className="text-right text-gray-500 text-xs uppercase tracking-wider px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => (
                      <tr key={product.id} className="border-b border-gray-800/30 hover:bg-white/[0.02] transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img src={product.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                            <span className="text-white text-sm truncate max-w-[200px]">{product.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-400 text-sm">{product.categoryName}</td>
                        <td className="px-6 py-4 text-white text-sm font-medium">&pound;{product.price.toFixed(2)}</td>
                        <td className="px-6 py-4">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${product.inStock ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                            {product.inStock ? 'In Stock' : 'Out of Stock'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => { setEditingProduct(product); setShowProductForm(true); }}
                              className="p-2 text-gray-500 hover:text-[#2EC866] transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteProduct(product.id)}
                              className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <h1 className="text-2xl font-bold text-white mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>Orders</h1>
            <div className="space-y-4">
              {orders.length === 0 ? (
                <p className="text-gray-500 text-center py-12">No orders yet</p>
              ) : (
                orders.map(order => (
                  <OrderCard key={order.id} order={order} onUpdateStatus={handleUpdateOrderStatus} />
                ))
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Product Form Component
const ProductForm = ({ product, categories, inputCls, onSave, onCancel, isEditing }) => {
  const [form, setForm] = useState({ ...product, featuresText: (product.features || []).join('\n') });
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(product.image || '');
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show local preview immediately
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error('Upload failed');

      const data = await res.json();
      const imageUrl = `${API_URL}/api/files/${data.path}`;
      setForm(f => ({ ...f, image: imageUrl }));
      setPreviewUrl(imageUrl);
      toast({ title: 'Image uploaded successfully' });
    } catch (err) {
      toast({ title: 'Image upload failed', description: err.message, variant: 'destructive' });
      setPreviewUrl(product.image || '');
    } finally {
      setUploading(false);
      URL.revokeObjectURL(localPreview);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.image) {
      toast({ title: 'Please upload an image', variant: 'destructive' });
      return;
    }
    const catOption = categories.find(c => c.value === form.category);
    onSave({
      ...form,
      price: parseFloat(form.price) || 0,
      categoryName: catOption ? catOption.label : form.categoryName,
      features: form.featuresText.split('\n').filter(f => f.trim()),
      images: [form.image]
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-gray-800/50 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-bold text-lg">{isEditing ? 'Edit Product' : 'Add Product'}</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Name *</label>
            <input data-testid="product-name-input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-xs mb-1.5 block">Price (£) *</label>
              <input data-testid="product-price-input" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))} required className={inputCls} />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1.5 block">Category *</label>
              <select data-testid="product-category-select" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className={`${inputCls} appearance-none cursor-pointer`}>
                {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Product Image *</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleImageUpload}
              className="hidden"
              data-testid="product-image-file-input"
            />
            {previewUrl ? (
              <div className="relative group">
                <img src={previewUrl} alt="Preview" className="w-full h-48 object-cover rounded-lg border border-gray-800" />
                <div
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center cursor-pointer"
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <div className="text-white text-sm flex items-center gap-2">
                      <Upload className="w-4 h-4" /> Change Image
                    </div>
                  )}
                </div>
                {uploading && (
                  <div className="absolute bottom-2 left-2 right-2 bg-black/70 rounded-md px-3 py-1.5 flex items-center gap-2">
                    <Loader2 className="w-3 h-3 text-[#2EC866] animate-spin" />
                    <span className="text-[#2EC866] text-xs">Uploading...</span>
                  </div>
                )}
              </div>
            ) : (
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed border-gray-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#2EC866]/50 transition-colors"
                data-testid="product-image-upload-zone"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-8 h-8 text-[#2EC866] animate-spin mb-2" />
                    <span className="text-gray-400 text-sm">Uploading...</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-8 h-8 text-gray-600 mb-2" />
                    <span className="text-gray-400 text-sm">Click to upload image</span>
                    <span className="text-gray-600 text-xs mt-1">JPG, PNG, WebP or GIF</span>
                  </>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Description</label>
            <textarea data-testid="product-description-input" rows={3} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className={`${inputCls} resize-none`} />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Features (one per line)</label>
            <textarea data-testid="product-features-input" rows={3} value={form.featuresText} onChange={(e) => setForm(f => ({ ...f, featuresText: e.target.value }))} className={`${inputCls} resize-none`} />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer">
              <input type="checkbox" checked={form.inStock} onChange={(e) => setForm(f => ({ ...f, inStock: e.target.checked }))} className="accent-[#2EC866]" />
              In Stock
            </label>
            <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm(f => ({ ...f, featured: e.target.checked }))} className="accent-[#2EC866]" />
              Featured
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel} className="flex-1 border border-gray-700 text-gray-400 hover:text-white py-2.5 rounded-lg text-sm transition-colors">
              Cancel
            </button>
            <button data-testid="product-save-btn" type="submit" disabled={uploading} className="flex-1 bg-[#2EC866] hover:bg-[#25a855] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg text-sm transition-colors flex items-center justify-center gap-2">
              <Save className="w-4 h-4" /> {isEditing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Order Card Component
const OrderCard = ({ order, onUpdateStatus }) => {
  const [expanded, setExpanded] = useState(false);
  const statusOptions = ['processing', 'shipped', 'delivered', 'cancelled'];

  return (
    <div className="bg-[#111] border border-gray-800/50 rounded-xl overflow-hidden">
      <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-4">
          <span className="text-white font-mono text-sm">{order.id}</span>
          <span className="text-gray-500 text-sm">{order.customerName}</span>
          <span className="text-gray-600 text-xs">{order.date}</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white font-bold text-sm">&pound;{order.total.toFixed(2)}</span>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            order.status === 'delivered' ? 'bg-green-500/10 text-green-400' :
            order.status === 'shipped' ? 'bg-blue-500/10 text-blue-400' :
            order.status === 'cancelled' ? 'bg-red-500/10 text-red-400' :
            'bg-yellow-500/10 text-yellow-400'
          }`}>{order.status}</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-800/30 pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-500 text-xs mb-1">Customer</p>
              <p className="text-white text-sm">{order.customerName}</p>
              <p className="text-gray-400 text-xs">{order.customerEmail}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Shipping Address</p>
              <p className="text-white text-sm">{order.shippingAddress}</p>
            </div>
          </div>
          <div className="mb-4">
            <p className="text-gray-500 text-xs mb-2">Items</p>
            {order.items.map((item, i) => (
              <div key={i} className="flex justify-between text-sm py-1">
                <span className="text-gray-300">{item.name} x{item.quantity}</span>
                <span className="text-white">&pound;{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-gray-500 text-xs">Update Status:</label>
            <select
              value={order.status}
              onChange={(e) => onUpdateStatus(order.id, e.target.value)}
              className="bg-[#0a0a0a] border border-gray-800 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-[#2EC866]/50 cursor-pointer appearance-none"
            >
              {statusOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
